import { ArrowLeftOutlined, ArrowRightOutlined } from "@ant-design/icons";
import { Button } from "react_reusable_components";
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useIntl } from "react-intl";
import { OneRow, ThreeRow, TwoColumn } from "../../core/layouts";
import { GlobalLoaderContext } from "../../globalContext/globalLoader/globalLoaderProvider";
import { AuditContext } from "../../globalContext/ManageAudit/AuditProvider";
import AuditService from "../../services/audit-service";
import variables from "../../themes/base/styles/variables";
import { getLocalizedMessage } from "../../UtilityFunctions/utils";
import AuditQuestionnaireContainer from "../AuditQuestionnaireContainer";
import Style from "./SingleQuestionnaireContainerStyles.module.scss";
import BreadCrumbContainer from "../BreadCrumbContainer";
import QuestionnaireNavigationContainer from "../QuestionnaireNavigationContainer";
import { useParams } from "react-router-dom";
import { AuthContext } from "../../globalContext/auth/authProvider";
import {
  AUDIT_STATUSES,
  EVALUATION_STATUS_CONSTANTS,
  userRole,
} from "../../constants";
import { RemoteAuditContext } from "../../globalContext/remoteAudit/remoteAuditProvider";
import {
  setAuditSubmitted,
  setCurrentQuestion,
} from "../../globalContext/remoteAudit/remoteAuditActions";
import { showNotification } from "../../UtilityFunctions/notification";
import useResponsive from "core/hooks/useResponsive";
import CreatePortalCard from "../../components/CreatePortalCard";
import ConfirmModalContainer from "../ConfirmModalContainer/ConfirmModalContainer";

const colorMap = {
  [EVALUATION_STATUS_CONSTANTS.NG]: variables.primaryBg,
  [EVALUATION_STATUS_CONSTANTS.OK]: variables.successGreen,
  [EVALUATION_STATUS_CONSTANTS.IMPROVEMENT]: variables.amberYellow,
  default: variables.grey4,
};

const SingleQuestionnaireContainerStyle = {
  mainWrapperStyle: {
    width: variables.fullWidth,
    height: variables.remoteMainSectionWrapperHeight,
  },
  questionnaireNButtonWrapperStyle: {
    gap: variables.gapSmall,
    overflow: "unset",
    height: variables.fullHeight,
  },
  questionnaireSectionWrapperStyle: {
    overflow: "unset",
    height: `calc(100% - ${variables.buttonHeightNormal} - ${variables.gapSmall})`,
  },
  questionThreeRowStyle: {
    backgroundColor: variables.bodyBg,
    border: `1px solid ${variables.grey4}`,
    height: variables.fullHeight,
    cursor: "default",
    display: "flex",
    padding: `${variables.paddingSmall} 0`,
    flexDirection: "column",
    gap: variables.gapSmaller,
    overflow: "hidden",
    zIndex: 4,
  },
  breadCrumbWrapperStyle: {
    width: variables.fullWidth,
    overflow: "unset",
    padding: `0 ${variables.paddingSmall}`,
  },
  mainQuestionSectionStyle: {
    overflow: "auto",
    height: variables.remoteQuestionSectionHeight,
    padding: `0 ${variables.paddingSmall}`,
  },
  buttonWrapperStyle: {
    width: variables.fullWidth,
    justifyContent: "space-between",
    overflow: "unset",
  },
  buttonSectionWrapperStyle: {
    padding: `0 ${variables.paddingSmall}`,
  },
  submitButtonSectionWrapperStyle: {
    justifyContent: "flex-end",
    width: "100%",
  },
};
const SingleQuestionnaireContainer = () => {
  const loaderDispatch = useContext(GlobalLoaderContext)[1];
  const [authState] = useContext(AuthContext);
  const [remoteAuditState, remoteAuditDispatch] =
    useContext(RemoteAuditContext);
  const [auditState, auditDispatch] = useContext(AuditContext);
  const [allQuestions, setAllQuestions] = useState([]);
  const [questionsData, setQuestionsData] = useState([
    {
      sectionName: "",
      sequence: "1",
    },
  ]);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [selected, setSelected] = useState(0);
  const [openNavigation, setOpenNavigation] = useState(false);
  const [borderAlert, setBorderAlert] = useState(false);
  const [openModal, setOpenModal] = useState();
  const intl = useIntl();
  const { auditId } = useParams();
  const questionRef = useRef();

  useEffect(() => {
    getQuestionnaireByIdApiCall();
  }, []);

  const getQuestionnaireByIdApiCall = () => {
    AuditService.getAuditQuestionnaireById({
      auditDispatch,
      loaderDispatch,
      auditId,
      intl,
    });
  };

  useEffect(() => {
    if (auditState?.auditDetail?.sections) {
      generateBreadcrumbs(auditState?.auditDetail?.sections, []);
      handleSetExpandedKeys(auditState?.auditDetail?.sections);
      setQuestionsData(auditState?.auditDetail?.sections);
    }
  }, [auditState?.auditDetail?.sections]);

  const generateBreadcrumbs = useCallback(
    (parentSections, parentCurrentBreadcrumb) => {
      setAllQuestions([]);
      function recursiveGenerateBreadcrumbs(sections, currentBreadcrumb) {
        for (const section of sections) {
          const newBreadcrumb = [...currentBreadcrumb, section.sectionName];

          if (section.sections) {
            recursiveGenerateBreadcrumbs(section.sections, newBreadcrumb);
          }

          if (section?.questions) {
            for (const question of section.questions) {
              setAllQuestions((prev) => [
                ...prev,
                {
                  breadCrumb: [...newBreadcrumb],//add question.name to add question also
                  question: question,
                },
              ]);
            }
          }
        }
      }

      recursiveGenerateBreadcrumbs(parentSections, parentCurrentBreadcrumb);
    },
    []
  );

  function handleSetExpandedKeys(data) {
    for (const item of data) {
      if (item?.sections) {
        const section = item;
        if (section?.sequence) {
          setExpandedKeys((prev) => [...prev, section.sequence]);
        }
        handleSetExpandedKeys(section?.sections);
      }
    }
  }

  useEffect(() => {
    if (selected) {
      const index = allQuestions?.findIndex(
        (que) => que.question.id === selected
      );
      changeQuestion(index);
    }
  }, [selected]);

  const changeQuestion = (newQuestionIndex) => {
    if (!remoteAuditState?.isEvidenceCompleted) {
      showNotification(
        "error",
        "Error",
        "Save the current recording in progress before moving to the next question."
      );
      return;
    }
    questionRef.current.saveQuestion();
    remoteAuditDispatch(setCurrentQuestion(newQuestionIndex));
  };

  const hasValidResponse = (question) => {
    // const validResponse = question.questionType?.some(
    //   (questionTypeItems) =>
    //     questionTypeItems?.response?.length > 0 ||
    //     !isNaN(questionTypeItems?.response)
    // );
    
    let valid = false;
    if (authState?.currentRole?.id === userRole.TM) {
      valid = question?.questionResponse?.evaluationStatus;
    } else {
      valid = question?.questionResponse?.textResponse;
    }
    return valid;
  };

  useEffect(() => {
    const isAuditSubmitted =
      remoteAuditState?.auditSubmitted ||
      auditState?.auditDetail?.auditStatus === AUDIT_STATUSES.AUDIT_SUBMITTED;
    if (isAuditSubmitted) {
      remoteAuditDispatch(setAuditSubmitted(isAuditSubmitted));
      showNotification(
        "success",
        "Success",
        getLocalizedMessage(intl, "manageAudit.F2F.auditSubmittedSuccess")
      );
    }
  }, [remoteAuditState?.auditSubmitted, auditState?.auditDetail?.auditStatus]);

  const hasCompulsoryQuestionWithInvalidResponse = (sections, returnValue) => {
    for (const section of sections) {
      if (section.questions) {
        for (const question of section.questions) {
          if (
            (question?.isMandatory || returnValue) &&
            !hasValidResponse(question)
          ) {
            if (returnValue) return `${section.sequence}${question.sequence}`;
            else return true;
          }
        }
      }

      if (section.sections) {
        const hasInvalidResponse = hasCompulsoryQuestionWithInvalidResponse(
          section.sections,
          returnValue
        );
        if (hasInvalidResponse) {
          return hasInvalidResponse;
        }
      }
    }
    return false;
  };

  const onSubmit = () => {
    questionRef.current.saveQuestion();
    AuditService.getAuditQuestionnaireById({
      auditDispatch,
      auditId,
      intl,
    }).then((res) => {
      const auditPending = res?.data?.auditStatus === AUDIT_STATUSES.PENDING;
      if (auditPending) {
        showNotification(
          "error",
          "Error",
          getLocalizedMessage(intl, "manageAudit.fillAtLeastOneResponse")
        );
        return;
      }
      if (res?.data?.auditStatus === AUDIT_STATUSES.RE_AUDIT_PENDING) {
        showNotification(
          "error",
          "Error",
          getLocalizedMessage(
            intl,
            "manageAudit.fillAtLeastOneResponse.reAuditPending"
          )
        );
        return;
      }
      const notReadyToSubmit = hasCompulsoryQuestionWithInvalidResponse(
        res?.data?.sections,
        false
      );
      if (!notReadyToSubmit) {
        AuditService.updateAuditStatus({
          loaderDispatch,
          intl,
          body: { status: AUDIT_STATUSES.AUDIT_SUBMITTED },
          auditId,
          successMessageCode: "manageAudit.F2F.auditSubmittedSuccess",
        }).then(() => {
          remoteAuditDispatch(setAuditSubmitted(true));
        });
      } else {
        showNotification(
          "error",
          "Error",
          getLocalizedMessage(intl, "manageAudit.F2F.mandatoryFieldsError")
        );
      }
    });
  };
  const responsive = useResponsive();

  return (
    <div
      style={{
        ...SingleQuestionnaireContainerStyle.mainWrapperStyle,
      }}
    >
      <QuestionnaireNavigationContainer
        questionsData={questionsData}
        questionnaireName={auditState?.auditDetail?.questionnaireName}
        absolute={true}
        expandedKeys={expandedKeys}
        setExpandedKeys={setExpandedKeys}
        setSelected={setSelected}
        openNavigation={openNavigation}
        setOpenNavigation={setOpenNavigation}
        handleSetExpandedKeys={handleSetExpandedKeys}
      />
      <CreatePortalCard id="root" open={!responsive?.isSm}>
        <OneRow
          style={{
            ...SingleQuestionnaireContainerStyle.questionnaireNButtonWrapperStyle,
            ...(!responsive?.isSm && {
              position: "absolute",
              top: "9.75rem",
              right: "1rem",
              width: "85vw",
              height: "calc(100% - 14.75rem)",
              alignItems: "flex-end",
            }),
          }}
          sectionStyle={{
            ...SingleQuestionnaireContainerStyle.questionnaireSectionWrapperStyle,
            ...(!responsive.isSm && {
              width: "100%",
              height: "100%",
            }),
            ...(!responsive?.isSm &&
              !remoteAuditState?.showQuestion && {
                zIndex: -1,
              }),
          }}
          section={
            !!allQuestions?.length && (
              <ThreeRow
                style={{
                  ...SingleQuestionnaireContainerStyle.questionThreeRowStyle,
                  ...{
                    border: `1px solid ${
                      colorMap[borderAlert] || colorMap.default
                    }`,
                  },
                }}
                topSectionStyle={
                  SingleQuestionnaireContainerStyle.breadCrumbWrapperStyle
                }
                topSection={
                  <BreadCrumbContainer
                    data={
                      allQuestions?.[remoteAuditState?.currentQuestion]
                        ?.breadCrumb
                    }
                  />
                }
                middleSectionStyle={
                  SingleQuestionnaireContainerStyle.mainQuestionSectionStyle
                }
                middleSection={
                  <AuditQuestionnaireContainer
                    setBorderAlert={setBorderAlert}
                    question={
                      allQuestions?.[remoteAuditState?.currentQuestion]
                        ?.question
                    }
                    mode={
                      authState?.currentRole?.id !== userRole.TM ||
                      remoteAuditState.auditSubmitted
                        ? "view"
                        : "edit"
                    }
                    outerRef={questionRef}
                  />
                }
                bottomSectionStyle={{
                  ...SingleQuestionnaireContainerStyle.buttonSectionWrapperStyle,
                  ...(authState?.currentRole?.id !== userRole.TM && {
                    display: "none",
                  }),
                }}
                bottomSection={
                  <TwoColumn
                    style={SingleQuestionnaireContainerStyle.buttonWrapperStyle}
                    leftSectionStyle={{
                      visibility:
                        remoteAuditState?.currentQuestion === 0 && "hidden",
                    }}
                    leftSection={
                      <Button
                        className={Style["prev-next-btn"]}
                        ghost
                        onClick={() => {
                          if (remoteAuditState?.currentQuestion !== 0)
                            changeQuestion(
                              remoteAuditState?.currentQuestion - 1
                            );
                        }}
                        icon={<ArrowLeftOutlined />}
                      >
                        {getLocalizedMessage(intl, "button.text.prevQuestion")}
                      </Button>
                    }
                    rightSection={
                      remoteAuditState?.currentQuestion <
                        allQuestions?.length - 1 && (
                        <Button
                          className={Style["prev-next-btn"]}
                          ghost
                          onClick={() => {
                            if (
                              remoteAuditState?.currentQuestion !==
                              allQuestions?.length - 1
                            )
                              changeQuestion(
                                remoteAuditState?.currentQuestion + 1
                              );
                          }}
                          icon={<ArrowRightOutlined />}
                        >
                          {getLocalizedMessage(
                            intl,
                            "button.text.nextQuestion"
                          )}
                        </Button>
                      )
                    }
                  />
                }
              />
            )
          }
          bottomSectionStyle={{
            ...SingleQuestionnaireContainerStyle.submitButtonSectionWrapperStyle,
            ...(authState?.currentRole?.id !== userRole.TM && {
              display: "none",
            }),
            ...(!responsive.isSm && {
              width: "fit-content",
            }),
          }}
          bottomSection={
            <Button
              className={Style["submit-btn"]}
              icon={null}
              onClick={onSubmit}
              disabled={remoteAuditState.auditSubmitted}
            >
              {getLocalizedMessage(intl, "button.submit")}
            </Button>
          }
        />
      </CreatePortalCard>
      {authState?.currentRole?.id === userRole.TM && (
        <Button
          className={Style["submit-btn"]}
          icon={null}
          onClick={() => setOpenModal(true)}
          style={{
            position: "absolute",
            zIndex: 9999,
            bottom: "5rem",
            right: "1rem",
          }}
          disabled={remoteAuditState.auditSubmitted}
        >
          {getLocalizedMessage(intl, "button.submit")}
        </Button>
      )}
      <ConfirmModalContainer
        question={getLocalizedMessage(intl, "audit.submit.confirmation")}
        open={openModal}
        setOpen={setOpenModal}
        iconSrc={"/icons/warningIcon.svg"}
        onClose={() => {
          setOpenModal(false);
        }}
        onCancel={() => {
          setOpenModal(false);
        }}
        onConfirm={onSubmit}
      />
    </div>
  );
};

export default SingleQuestionnaireContainer;
